/**
 * Annotation scale store (Faz 8.4 — OCS annotative parity, pragmatic model).
 *
 * OCS keeps the scale list in the ACAD_SCALELIST dictionary and per-entity
 * ObjectContextData leaves; yap carries a persisted named-scale list plus
 * per-entity data flags:
 *   data.annotative    — annotative flag
 *   data.annoNativeScale — the scale name the entity was authored under
 *   data.annoScales    — Record<scaleName, { offset?, rotation?, scale? }>
 *                        (OCS ObjectContextData core: membership per scale)
 *
 * DWG-native ObjectContextData write-back stays out of scope (Faz 1 note).
 * Render consumption: effective multiplier = currentFactor / nativeFactor;
 * visibility rule mirrors OCS annotative_offscale_for: an entity with
 * explicit memberships that exclude the current scale hides while the
 * AnnoVisibility pill is on.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useMemo } from 'react';
import { settingsStorage } from '../utils/settingsPersistence';
import { useAppStore } from './store';
import { useSystemVarsStore } from './useSystemVarsStore';

/** One named annotation scale ("1:100" → drawing units per paper unit). */
export interface AnnotationScale {
    name: string;
    /** Scale factor as a plain multiplier (1:100 → 100, 2:1 → 0.5). */
    factor: number;
}

/** AutoCAD standard metric scale list (SCALELISTEDIT seed). */
const STANDARD_SCALES: AnnotationScale[] = [
    { name: '1:1000', factor: 1000 },
    { name: '1:500', factor: 500 },
    { name: '1:200', factor: 200 },
    { name: '1:100', factor: 100 },
    { name: '1:75', factor: 75 },
    { name: '1:50', factor: 50 },
    { name: '1:40', factor: 40 },
    { name: '1:30', factor: 30 },
    { name: '1:20', factor: 20 },
    { name: '1:16', factor: 16 },
    { name: '1:10', factor: 10 },
    { name: '1:8', factor: 8 },
    { name: '1:5', factor: 5 },
    { name: '1:4', factor: 4 },
    { name: '1:2', factor: 2 },
    { name: '1:1', factor: 1 },
    { name: '2:1', factor: 0.5 },
    { name: '4:1', factor: 0.25 },
    { name: '5:1', factor: 0.2 },
    { name: '10:1', factor: 0.1 },
    { name: '20:1', factor: 0.05 },
    { name: '50:1', factor: 0.02 },
    { name: '100:1', factor: 0.01 },
];

/** Per-scale representation core (OCS ObjectContextData subset). */
export interface AnnoScaleData {
    offset?: [number, number];
    rotation?: number;
    scale?: number;
}

interface AnnotationScaleState {
    /** The scale list (SCALELISTEDIT surface). */
    scaleList: AnnotationScale[];
    /** Current annotation scale NAME (CANNOSCALE parity). */
    currentScale: string;
    /** AnnoVisibility pill: hide annotative objects whose membership
     *  excludes the current scale (OCS all_visible=false). */
    annoVisibility: boolean;
    /** AnnoAutoAdd pill: stamp the current scale onto newly-marked
     *  annotative objects automatically. */
    annoAutoAdd: boolean;
    /** VpScaleSync pill: a paper-space viewport scale change propagates
     *  to the current annotation scale. */
    vpScaleSync: boolean;

    setCurrentScale: (name: string) => void;
    addScale: (name: string, factor: number) => void;
    removeScale: (name: string) => void;
    renameScale: (oldName: string, newName: string) => void;
    setPill: (pill: 'annoVisibility' | 'annoAutoAdd' | 'vpScaleSync', on: boolean) => void;

    /** Factor of the named scale (1.0 when unknown). */
    factorOf: (name: string | undefined) => number;
}

export const useAnnotationScaleStore = create<AnnotationScaleState>()(
    persist(
        (set, get) => ({
            scaleList: STANDARD_SCALES,
            currentScale: '1:100',
            annoVisibility: true,
            annoAutoAdd: true,
            vpScaleSync: true,

            setCurrentScale: (name) => {
                if (!get().scaleList.some((s) => s.name === name)) return;
                set({ currentScale: name });
                // CANNOSCALE parity: the sysvar mirrors the denominator.
                const factor = get().factorOf(name);
                const denom = Math.round(factor);
                useSystemVarsStore.getState().set('CANNOSCALE', denom > 0 ? denom : 1);
            },

            addScale: (name, factor) => set((s) => (
                s.scaleList.some((x) => x.name === name) || !name.trim() || !(factor > 0)
                    ? s
                    : { scaleList: [...s.scaleList, { name: name.trim(), factor }] }
            )),
            removeScale: (name) => set((s) => {
                if (s.scaleList.length <= 1 || name === s.currentScale) return s;
                return { scaleList: s.scaleList.filter((x) => x.name !== name) };
            }),
            renameScale: (oldName, newName) => set((s) => {
                const trimmed = newName.trim();
                if (!trimmed || s.scaleList.some((x) => x.name === trimmed)) return s;
                return {
                    scaleList: s.scaleList.map((x) => (x.name === oldName ? { ...x, name: trimmed } : x)),
                    currentScale: s.currentScale === oldName ? trimmed : s.currentScale,
                };
            }),

            setPill: (pill, on) => set((s) => (s[pill] === on ? s : { [pill]: on })),

            factorOf: (name) => get().scaleList.find((s) => s.name === name)?.factor ?? 1,
        }),
        {
            name: 'yap-annotation-scales',
            storage: settingsStorage,
            partialize: (s) => ({
                scaleList: s.scaleList,
                currentScale: s.currentScale,
                annoVisibility: s.annoVisibility,
                annoAutoAdd: s.annoAutoAdd,
                vpScaleSync: s.vpScaleSync,
            }),
        }
    )
);

// ---------------------------------------------------------------------------
// Entity-side helpers (data.annotative / data.annoNativeScale / annoScales)
// ---------------------------------------------------------------------------

/** Read an entity's annotative membership bag (undefined when not annotative). */
export function annoScalesOf(data: Record<string, any> | undefined): Record<string, AnnoScaleData> | undefined {
    if (!data?.annotative) return undefined;
    const scales = data.annoScales;
    return scales && typeof scales === 'object' ? (scales as Record<string, AnnoScaleData>) : {};
}

/**
 * Mark entities annotative (OBJECTSCALE ADD core): with AnnoAutoAdd on, the
 * current scale joins the membership; without it the object is annotative
 * but scaleless (visible at every scale, multiplier 1.0) until OBJECTSCALE
 * adds one explicitly.
 */
export function markAnnotative(datas: Record<string, any>[]): Record<string, any>[] {
    const { currentScale, annoAutoAdd } = useAnnotationScaleStore.getState();
    return datas.map((d) => {
        if (d.annotative) return d;
        return {
            ...d,
            annotative: true,
            annoNativeScale: currentScale,
            annoScales: annoAutoAdd ? { [currentScale]: {} } : {},
        };
    });
}

/** Remove the annotative state entirely (OBJECTSCALE DELETE all). */
export function unmarkAnnotative(d: Record<string, any>): Record<string, any> {
    const out = { ...d };
    delete out.annotative;
    delete out.annoNativeScale;
    delete out.annoScales;
    return out;
}

/** Add one scale to an entity's membership (OBJECTSCALE ADD <scale>). */
export function addAnnoScale(d: Record<string, any>, scaleName: string): Record<string, any> {
    const memberships = { ...(annoScalesOf(d) ?? {}) };
    memberships[scaleName] = memberships[scaleName] ?? {};
    return { ...d, annotative: true, annoNativeScale: d.annoNativeScale ?? scaleName, annoScales: memberships };
}

/** Remove one scale from an entity's membership (OBJECTSCALE DELETE). */
export function removeAnnoScale(d: Record<string, any>, scaleName: string): Record<string, any> {
    const memberships = { ...(annoScalesOf(d) ?? {}) };
    delete memberships[scaleName];
    return { ...d, annoScales: memberships };
}

/** ANNORESET: drop memberships, keep the native scale as the only one. */
export function resetAnnoScales(d: Record<string, any>): Record<string, any> {
    if (!d.annotative) return d;
    const native = String(d.annoNativeScale ?? useAnnotationScaleStore.getState().currentScale);
    return { ...d, annoScales: { [native]: {} } };
}

/**
 * Render multiplier for an annotative entity: current factor / native
 * factor (OCS effective_annotation_scale). Non-annotative → 1.0.
 */
export function annoMultiplierOf(data: Record<string, any> | undefined): number {
    if (!data?.annotative) return 1;
    const store = useAnnotationScaleStore.getState();
    const current = store.factorOf(store.currentScale);
    const native = store.factorOf(String(data.annoNativeScale ?? ''));
    if (native <= 0) return 1;
    return current / native;
}

/**
 * Visibility rule (OCS annotative_offscale_for): hide when AnnoVisibility
 * is on AND the entity is annotative AND carries explicit memberships AND
 * none matches the current scale.
 */
export function annoHiddenForScale(data: Record<string, any> | undefined): boolean {
    if (!data?.annotative) return false;
    const store = useAnnotationScaleStore.getState();
    if (!store.annoVisibility) return false;
    const memberships = annoScalesOf(data);
    if (!memberships || Object.keys(memberships).length === 0) return false;
    return !(store.currentScale in memberships);
}

/** Entity types the annotative model consumes (OCS supported-type list). */
const ANNOTATIVE_TYPES = new Set(['text', 'mtext', 'dimension', 'leader', 'tolerance', 'hatch', 'attdef', 'insert']);

/** True when the entity type can carry annotative data. */
export function isAnnotativeType(type: string): boolean {
    return ANNOTATIVE_TYPES.has(type);
}

/** VpScaleSync (Faz 8.12): propagate a viewport scale factor to the current
 *  annotation scale by picking the listed scale with the closest factor. */
export function syncAnnotationScaleToViewport(factor: number): void {
    const store = useAnnotationScaleStore.getState();
    if (!store.vpScaleSync || !(factor > 0)) return;
    let best = store.scaleList[0];
    let bestDiff = Infinity;
    for (const s of store.scaleList) {
        const diff = Math.abs(Math.log(s.factor) - Math.log(factor));
        if (diff < bestDiff) { bestDiff = diff; best = s; }
    }
    if (best && best.name !== store.currentScale) store.setCurrentScale(best.name);
}

/** OBJECTSCALE runner core: add/remove/reset annotative state on selection. */
export function applyObjectScale(
    ids: string[],
    mode: 'add' | 'remove' | 'reset',
    scaleName?: string,
): number {
    const app = useAppStore.getState();
    const touched = ids.filter((id) => {
        const e = app.entities[id];
        return !!e && isAnnotativeType(e.type);
    });
    if (touched.length === 0) return 0;
    const before = new Map<string, Record<string, any>>();
    const after = new Map<string, Record<string, any>>();
    touched.forEach((id) => {
        const e = app.entities[id];
        before.set(id, structuredClone(e.data));
        let next = structuredClone(e.data);
        if (mode === 'add') {
            next = scaleName ? addAnnoScale(next, scaleName) : markAnnotative([next])[0];
        } else if (mode === 'remove') {
            next = scaleName ? removeAnnoScale(next, scaleName) : unmarkAnnotative(next);
        } else {
            next = resetAnnoScales(next);
        }
        after.set(id, next);
        app.updateEntity(id, { data: next });
    });
    app.pushHistory({
        type: 'cmd.patch',
        timestamp: Date.now(),
        data: { count: touched.length, label: 'OBJECTSCALE' },
        undo: () => {
            const a = useAppStore.getState();
            before.forEach((d, id) => { if (a.entities[id]) a.updateEntity(id, { data: d }); });
        },
        redo: () => {
            const a = useAppStore.getState();
            after.forEach((d, id) => { if (a.entities[id]) a.updateEntity(id, { data: d }); });
        },
    });
    return touched.length;
}

// ---------------------------------------------------------------------------
// Reactive render hooks (Faz 8.4 consumption)
// ---------------------------------------------------------------------------

/** Annotative size multiplier for one entity's data bag, reactive to the
 *  current scale and scale list (1.0 for non-annotative data). */
export function useAnnoMultiplier(data: Record<string, any> | undefined): number {
    const currentScale = useAnnotationScaleStore((s) => s.currentScale);
    const scaleList = useAnnotationScaleStore((s) => s.scaleList);
    return useMemo(() => {
        if (!data?.annotative) return 1;
        const factorOf = (name: string) => scaleList.find((s) => s.name === name)?.factor ?? 1;
        const current = factorOf(currentScale);
        const native = factorOf(String(data.annoNativeScale ?? ''));
        return native > 0 ? current / native : 1;
    }, [data?.annotative, data?.annoNativeScale, currentScale, scaleList]);
}

/** Visibility gate (OCS annotative_offscale_for), reactive to the pill and
 *  the current scale. */
export function useAnnoHidden(data: Record<string, any> | undefined): boolean {
    const annoVisibility = useAnnotationScaleStore((s) => s.annoVisibility);
    const currentScale = useAnnotationScaleStore((s) => s.currentScale);
    return useMemo(
        () => annoHiddenForScale(data),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data?.annotative, data?.annoScales, annoVisibility, currentScale],
    );
}
